import { ClassSchedule } from '@/types/classTypes';

export interface ScheduleConflict {
  id: string;
  type: 'overlap' | 'back_to_back' | 'overbooked';
  severity: 'warning' | 'error';
  message: string;
  conflictingSchedules: string[]; // Array of schedule IDs
  details?: {
    overlapMinutes?: number;
    suggestedAction?: string;
  };
}

/**
 * Utility functions for detecting and managing schedule conflicts
 */
export class ScheduleConflictDetector {
  /**
   * Convert time string (HH:MM:SS) to minutes since midnight
   */
  private static timeToMinutes(timeString?: string): number {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  }

  /**
   * Convert minutes since midnight back to time string
   */
  private static minutesToTime(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:00`;
  }

  /**
   * Check if two time ranges overlap
   */
  private static timeRangesOverlap(
    start1: number, end1: number,
    start2: number, end2: number
  ): { overlaps: boolean; overlapMinutes: number } {
    const overlapStart = Math.max(start1, start2);
    const overlapEnd = Math.min(end1, end2);
    const overlapMinutes = Math.max(0, overlapEnd - overlapStart);
    
    return {
      overlaps: overlapMinutes > 0,
      overlapMinutes
    };
  }

  /**
   * Detect all conflicts for a given set of schedules
   */
  static detectConflicts(schedules: ClassSchedule[]): ScheduleConflict[] {
    const conflicts: ScheduleConflict[] = [];
    const processedPairs = new Set<string>();

    // Group schedules by day
    const schedulesByDay = schedules.reduce((acc, schedule) => {
      if (schedule.weekday === undefined) return acc;
      
      const day = schedule.weekday;
      if (!acc[day]) acc[day] = [];
      acc[day].push(schedule);
      return acc;
    }, {} as Record<number, ClassSchedule[]>);

    // Check for conflicts within each day
    Object.entries(schedulesByDay).forEach(([day, daySchedules]) => {
      for (let i = 0; i < daySchedules.length; i++) {
        for (let j = i + 1; j < daySchedules.length; j++) {
          const schedule1 = daySchedules[i];
          const schedule2 = daySchedules[j];
          
          // Create unique pair identifier
          const pairId = [schedule1.id, schedule2.id].sort().join('-');
          if (processedPairs.has(pairId)) continue;
          processedPairs.add(pairId);

          const conflict = this.detectPairwiseConflict(schedule1, schedule2);
          if (conflict) {
            conflicts.push(conflict);
          }
        }
      }
    });

    return conflicts;
  }

  /**
   * Detect conflicts between two specific schedules
   */
  private static detectPairwiseConflict(
    schedule1: ClassSchedule,
    schedule2: ClassSchedule
  ): ScheduleConflict | null {
    // Must be on the same day to conflict
    if (schedule1.weekday !== schedule2.weekday) return null;

    const start1 = this.timeToMinutes(schedule1.start_time);
    const end1 = this.timeToMinutes(schedule1.end_time);
    const start2 = this.timeToMinutes(schedule2.start_time);
    const end2 = this.timeToMinutes(schedule2.end_time);

    const { overlaps, overlapMinutes } = this.timeRangesOverlap(start1, end1, start2, end2);

    if (overlaps) {
      // Direct overlap - this is an error
      return {
        id: `conflict-${schedule1.id}-${schedule2.id}`,
        type: 'overlap',
        severity: 'error',
        message: `Schedule overlap detected: ${overlapMinutes} minutes`,
        conflictingSchedules: [schedule1.id, schedule2.id],
        details: {
          overlapMinutes,
          suggestedAction: 'Adjust schedule times to eliminate overlap'
        }
      };
    }

    // Check for back-to-back scheduling (potential warning)
    const gap1to2 = start2 - end1; // Minutes between end of 1 and start of 2
    const gap2to1 = start1 - end2; // Minutes between end of 2 and start of 1
    const minGap = Math.min(Math.abs(gap1to2), Math.abs(gap2to1));

    if (minGap <= 15 && minGap > 0) { // Less than 15 minutes gap
      return {
        id: `backtoback-${schedule1.id}-${schedule2.id}`,
        type: 'back_to_back',
        severity: 'warning',
        message: `Back-to-back scheduling: Only ${minGap} minutes between classes`,
        conflictingSchedules: [schedule1.id, schedule2.id],
        details: {
          suggestedAction: 'Consider adding buffer time between classes'
        }
      };
    }

    return null;
  }

  /**
   * Get conflicts for a specific schedule
   */
  static getConflictsForSchedule(
    scheduleId: string,
    allConflicts: ScheduleConflict[]
  ): ScheduleConflict[] {
    return allConflicts.filter(conflict => 
      conflict.conflictingSchedules.includes(scheduleId)
    );
  }

  /**
   * Check if a new schedule would create conflicts
   */
  static validateNewSchedule(
    newSchedule: Partial<ClassSchedule>,
    existingSchedules: ClassSchedule[]
  ): ScheduleConflict[] {
    if (!newSchedule.weekday || !newSchedule.start_time || !newSchedule.end_time) {
      return [];
    }

    // Create a temporary schedule object for conflict detection
    const tempSchedule: ClassSchedule = {
      id: 'temp-schedule',
      class_id: newSchedule.class_id || '',
      organization_id: newSchedule.organization_id || '',
      weekday: newSchedule.weekday,
      start_time: newSchedule.start_time,
      end_time: newSchedule.end_time,
      name: newSchedule.name || '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    // Check against existing schedules on the same day
    const sameDay = existingSchedules.filter(s => s.weekday === newSchedule.weekday);
    const conflicts: ScheduleConflict[] = [];

    for (const existing of sameDay) {
      const conflict = this.detectPairwiseConflict(tempSchedule, existing);
      if (conflict) {
        // Update the conflict to reflect it's for a new schedule
        conflict.id = `new-schedule-conflict-${existing.id}`;
        conflict.conflictingSchedules = ['new-schedule', existing.id];
        conflicts.push(conflict);
      }
    }

    return conflicts;
  }

  /**
   * Get conflict summary for UI display
   */
  static getConflictSummary(conflicts: ScheduleConflict[]): {
    errorCount: number;
    warningCount: number;
    totalCount: number;
    topConflict?: ScheduleConflict;
  } {
    const errorCount = conflicts.filter(c => c.severity === 'error').length;
    const warningCount = conflicts.filter(c => c.severity === 'warning').length;
    const topConflict = conflicts.find(c => c.severity === 'error') || conflicts[0];

    return {
      errorCount,
      warningCount,
      totalCount: conflicts.length,
      topConflict
    };
  }

  /**
   * Get suggested resolution for a conflict
   */
  static getSuggestedResolution(conflict: ScheduleConflict): string {
    switch (conflict.type) {
      case 'overlap':
        return 'Move one schedule to a different time slot or adjust the duration';
      case 'back_to_back':
        return 'Add 15-30 minutes buffer between classes for setup/cleanup';
      case 'overbooked':
        return 'Reschedule one of the conflicting classes to a different day or time';
      default:
        return 'Review schedule timing and make necessary adjustments';
    }
  }
}